import { useCallback, useEffect, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack, Alert, CircularProgress, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, InputAdornment, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { card, btn, typo, inputSx, tbl } from '../styles/ds';
import { websiteRequest, fmtUsd } from './websiteApi';

/*
 * Website shipping rates (WS-7.1/7.2, WD-003/004; owner decisions 2026-11-04).
 * Per method (Israel / international) and class: quantity ranges with a price,
 * plus one flat price for other products. Above the last range the order is
 * sent for the admin's review — no price is invented. A mixed cart pays the
 * highest class price; pickup is always free. Every save is a new version.
 */

const METHODS = [
  { key: 'israel_domestic', title: 'משלוח בארץ (ישראל)' },
  { key: 'international', title: 'משלוח לחו"ל' },
];
const CLASSES = [
  { key: 'tefillin', title: 'תפילין', unit: 'זוגות' },
  { key: 'mezuzah', title: 'מזוזות', unit: 'מזוזות' },
];
const PREVIEW_METHODS = [
  { id: 'israel_domestic', label: 'משלוח בארץ' }, { id: 'international', label: 'משלוח לחו"ל' }, { id: 'pickup', label: 'איסוף עצמי' },
];

// Prices are edited in dollars and stored in cents; $0 is a valid price here.
const toInput = minor => (minor / 100).toString();
const toMinor = s => (/^\d+(\.\d{1,2})?$/.test(String(s).trim()) ? Math.round(Number(s) * 100) : NaN);

const toForm = rules => ({
  ...Object.fromEntries(METHODS.map(({ key }) => [key, {
    ...Object.fromEntries(CLASSES.map(c => [c.key, rules[key][c.key].map(b => ({ min: String(b.min), max: String(b.max), price: toInput(b.amountMinor) }))])),
    other: toInput(rules[key].otherMinor),
  }])),
  blocked: (rules.blockedCountries ?? []).join(', '),
});
const fromForm = form => ({
  ...Object.fromEntries(METHODS.map(({ key }) => [key, {
    ...Object.fromEntries(CLASSES.map(c => [c.key, form[key][c.key].map(b => ({ min: Number(b.min), max: Number(b.max), amountMinor: toMinor(b.price) }))])),
    otherMinor: toMinor(form[key].other),
  }])),
  blockedCountries: form.blocked.split(/[\s,]+/).filter(Boolean),
});

const BandTable = ({ bands, onChange, disabled, unit }) => {
  const set = (i, field, value) => onChange(bands.map((b, j) => (j === i ? { ...b, [field]: value } : b)));
  const add = () => {
    const last = bands.at(-1);
    const from = last ? Number(last.max) + 1 : 1;
    onChange([...bands, { min: String(from), max: String(from), price: last?.price ?? '0' }]);
  };
  const num = { inputMode: 'numeric', dir: 'ltr' };
  return (
    <>
      <Box className="crm-table-scroll" sx={tbl.container}>
        <Table size="small">
          <TableHead><TableRow><TableCell>מ-</TableCell><TableCell>עד ({unit})</TableCell><TableCell>מחיר</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {bands.map((b, i) => (
              <TableRow key={i}>
                <TableCell><TextField size="small" value={b.min} disabled={disabled} sx={{ ...inputSx, width: 80 }} inputProps={{ ...num, 'aria-label': `שורה ${i + 1} מ-` }} onChange={e => set(i, 'min', e.target.value)} /></TableCell>
                <TableCell><TextField size="small" value={b.max} disabled={disabled} sx={{ ...inputSx, width: 80 }} inputProps={{ ...num, 'aria-label': `שורה ${i + 1} עד` }} onChange={e => set(i, 'max', e.target.value)} /></TableCell>
                <TableCell>
                  <TextField size="small" value={b.price} disabled={disabled} sx={{ ...inputSx, width: 120 }}
                    inputProps={{ dir: 'ltr', inputMode: 'decimal', 'aria-label': `שורה ${i + 1} מחיר` }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    onChange={e => set(i, 'price', e.target.value)} />
                </TableCell>
                <TableCell padding="checkbox">
                  <IconButton size="small" aria-label={`מחיקת שורה ${i + 1}`} disabled={disabled || bands.length === 1}
                    onClick={() => onChange(bands.filter((_, j) => j !== i))}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Button size="small" startIcon={<AddIcon />} onClick={add} disabled={disabled} sx={{ mt: 1 }}>הוספת טווח</Button>
    </>
  );
};

const WebsiteShippingPage = () => {
  const notify = useNotify();
  const [loaded, setLoaded] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ method: 'international', countryCode: 'US', tefillin: '1', mezuzah: '0', other: '0' });
  const [previewResult, setPreviewResult] = useState(null);

  const load = useCallback(() => {
    websiteRequest('/website_shipping')
      .then(r => { setLoaded(r); setForm(toForm(r.rules)); })
      .catch(err => notify(err.message, { type: 'error' }));
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  if (!form) return <Box p={6} textAlign="center"><CircularProgress /></Box>;
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(loaded.rules));
  const setClass = (method, cls, bands) => setForm(f => ({ ...f, [method]: { ...f[method], [cls]: bands } }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await websiteRequest('/website_shipping', 'PUT', { version: loaded.version, rules: fromForm(form) });
      setLoaded(r); setForm(toForm(r.rules));
      notify('תעריפי המשלוח נשמרו', { type: 'success' });
    } catch (err) { notify(err.message, { type: 'error' }); }
    finally { setSaving(false); }
  };

  const runPreview = async () => {
    try {
      const units = { tefillin: Number(preview.tefillin) || 0, mezuzah: Number(preview.mezuzah) || 0, other: Number(preview.other) || 0 };
      setPreviewResult(await websiteRequest('/website_shipping/preview', 'POST',
        { rules: fromForm(form), method: preview.method, countryCode: preview.countryCode, units }));
    } catch (err) { setPreviewResult(null); notify(err.message, { type: 'error' }); }
  };

  return (
    <Box className="crm-page" sx={{ p: { xs: 1, sm: 3 } }}>
      <Title title="משלוחים" />
      <Typography {...typo.pageTitle} mb={1}>תעריפי משלוח לאתר</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {loaded.version === 0
          ? 'כרגע בתוקף הטבלה שאושרה באפיון. השמירה הראשונה יוצרת גרסה 1.'
          : `גרסה ${loaded.version} · נשמרה ${new Date(loaded.createdAt).toLocaleString('he-IL')}${loaded.createdBy ? ` על ידי ${loaded.createdBy}` : ''}`}
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        איסוף עצמי תמיד חינם. עגלה עם כמה סוגי מוצרים משלמת את המחיר הגבוה מביניהם (לא סכום).
        כמות מעל הטווח האחרון — ההזמנה נשלחת לבדיקת מנהל בלי מחיר משלוח, ואת הסכום קובעים באישור ההזמנה.
        קופון משלוח חינם נותן $0 גם מעל הטווחים.
      </Alert>

      {METHODS.map(m => (
        <Card key={m.key} sx={card.section}>
          <CardContent className="crm-card-content" sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography sx={{ ...typo.sectionTitle, mb: 2 }}>{m.title}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
              {CLASSES.map(c => (
                <Box key={c.key}>
                  <Typography fontWeight={600} mb={1}>{c.title}</Typography>
                  <BandTable bands={form[m.key][c.key]} unit={c.unit} disabled={saving} onChange={bands => setClass(m.key, c.key, bands)} />
                </Box>
              ))}
            </Box>
            <TextField label="מוצרים אחרים — מחיר קבוע" size="small" value={form[m.key].other} disabled={saving}
              sx={{ ...inputSx, mt: 3, width: 240 }} inputProps={{ dir: 'ltr', inputMode: 'decimal' }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              onChange={e => setForm(f => ({ ...f, [m.key]: { ...f[m.key], other: e.target.value } }))} />
          </CardContent>
        </Card>
      ))}

      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography sx={{ ...typo.sectionTitle, mb: 1 }}>מדינות שלא שולחים אליהן</Typography>
          <TextField fullWidth size="small" value={form.blocked} disabled={saving} sx={inputSx} placeholder="למשל: RU, IR"
            inputProps={{ dir: 'ltr', 'aria-label': 'מדינות חסומות' }}
            helperText="קודי מדינה של שתי אותיות באנגלית, מופרדים בפסיק. ריק — שולחים לכל המדינות. משלוח בארץ הוא לישראל בלבד."
            onChange={e => setForm(f => ({ ...f, blocked: e.target.value }))} />
        </CardContent>
      </Card>

      <Stack direction="row" spacing={1.5} mb={3} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button variant="contained" onClick={save} disabled={saving || !dirty} sx={btn.primary}>שמירה</Button>
        <Button variant="outlined" onClick={() => setForm(toForm(loaded.rules))} disabled={saving || !dirty} sx={btn.cancel}>ביטול שינויים</Button>
        {dirty && <Typography sx={typo.meta}>יש שינויים שלא נשמרו.</Typography>}
      </Stack>

      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography sx={{ ...typo.sectionTitle, mb: 0.5 }}>בדיקה: כמה יעלה משלוח?</Typography>
          <Typography sx={{ ...typo.meta, mb: 2 }}>מחשב לפי התעריפים שבטופס (גם לפני שמירה), באותו חישוב שהאתר משתמש בו.</Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
            <TextField select size="small" label="שיטה" value={preview.method} sx={{ ...inputSx, width: 160 }}
              onChange={e => setPreview(p => ({ ...p, method: e.target.value }))}>
              {PREVIEW_METHODS.map(o => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
            </TextField>
            {preview.method === 'international' && (
              <TextField size="small" label="מדינה" value={preview.countryCode} sx={{ ...inputSx, width: 90 }} inputProps={{ dir: 'ltr', maxLength: 2 }}
                onChange={e => setPreview(p => ({ ...p, countryCode: e.target.value.toUpperCase() }))} />
            )}
            {[['tefillin', 'תפילין'], ['mezuzah', 'מזוזות'], ['other', 'אחר']].map(([k, label]) => (
              <TextField key={k} size="small" label={label} value={preview[k]} sx={{ ...inputSx, width: 90 }} inputProps={{ dir: 'ltr', inputMode: 'numeric' }}
                onChange={e => setPreview(p => ({ ...p, [k]: e.target.value }))} />
            ))}
            <Button variant="outlined" onClick={runPreview}>חישוב</Button>
          </Stack>
          {previewResult && (
            <Alert sx={{ mt: 2 }} severity={previewResult.blocked || previewResult.state !== 'priced' ? 'warning' : 'success'}>
              {previewResult.blocked ? 'לא שולחים למדינה הזו במשלוח לחו"ל.'
                : previewResult.state === 'priced' ? `מחיר משלוח: ${fmtUsd(previewResult.amountMinor)}`
                  : 'מעל הטווחים — ההזמנה תישלח לבדיקת מנהל בלי מחיר משלוח.'}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default WebsiteShippingPage;
